import * as React from 'react'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import {
  PerformanceAnalyticsReportOverviewDocument,
  ReviewProcessTargetsManagedByFilterInput,
} from 'generated/resources/graphql'
import range from 'lodash/range'

import Callout from 'modules/core/components/Callout'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { EmptyState } from 'modules/performance_process/components/insights/Answers/common/EmptyState'
import { GaussBell } from 'modules/performance_process/components/insights/Overview/PerformanceScoreGaussbell'
import {
  PERFORMANCE_COLORS,
  isLowPerformer,
  isTopPerformer,
} from 'modules/performance_process/components/insights/Overview/PerformanceScoreGaussbell/scorePerformance'

export const Analytics: React.FC<{
  reviewId: number
  managedByFilter: ReviewProcessTargetsManagedByFilterInput | undefined
}> = ({ reviewId, managedByFilter }) => {
  const { data, loading, error } = useQuery(PerformanceAnalyticsReportOverviewDocument, {
    variables: {
      reviewId: reviewId,
      analyticManagerFilter: managedByFilter,
    },
  })

  const analytics = data?.performance?.reviewProcess?.analyticsSummary

  if (!analytics) return null

  const scores = analytics.managerGivenPerformanceScores
  const total = scores.length
  const averageScore = scores.reduce((acc, datum) => acc + Math.round(datum), 0) / total

  const scaleMin = analytics.defaultRatingScaleMin
  const scaleMax = analytics.defaultRatingScaleMax
  const scale = range(scaleMin, scaleMax + 1)

  const getScoreColor = (score: number) => {
    if (isTopPerformer(scaleMin, scaleMax, score)) return PERFORMANCE_COLORS.top
    if (isLowPerformer(scaleMin, scaleMax, score)) return PERFORMANCE_COLORS.low
    return PERFORMANCE_COLORS.normal
  }

  const scoreCount = scale.map((score) => {
    return {
      id: score,
      score: score,
      color: getScoreColor(score),
      count: scores.filter((datum) => Math.round(datum) == score).length,
    }
  })

  return (
    <Box>
      {loading || error ? (
        <Box minHeight="s400" alignItems="center" justifyContent="center">
          <Spinner />
        </Box>
      ) : analytics.managerGivenPerformanceScores.length > 0 ? (
        <Box gap="s16">
          <Callout
            description={i18n.t('performance_process.overview.manager_rating_answers.callout')}
            type="info"
            compact
          />
          <GaussBell
            title={i18n.t('performance_process.insights.charts.score_distribution')}
            data={{ count: scoreCount, total: total, average: averageScore }}
          />
        </Box>
      ) : (
        <EmptyState title={i18n.t('performance_process.insights.answers.empty_state_analytics')} />
      )}
    </Box>
  )
}
